import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Maybe } from 'types';
import { PLAYER_STATE, type PlayerState } from './playerApi';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StreamPlayerInstance = any;

const VIDEO_JUMP_TIME = 1 / 25;
const AUDIO_JUMP_TIME = 1;

function handleKeyPress(videoElement: HTMLVideoElement, isAudio: boolean, duration: number) {
    let playPromise: Maybe<PromiseLike<void>> = null;
    return function handle(event: KeyboardEvent) {
        if (!document.activeElement?.className.includes('stream-player')) {
            return;
        }

        const jumpTime = isAudio ? AUDIO_JUMP_TIME : VIDEO_JUMP_TIME;

        const normalizeTime = (time: number) => Math.max(0, Math.min(time, duration));

        const { currentTime } = videoElement;

        switch (event.key) {
            case 'j':
                event.preventDefault();
                // eslint-disable-next-line no-param-reassign
                videoElement.currentTime = normalizeTime(currentTime - jumpTime);
                videoElement.pause();
                break;

            case 'k':
                event.preventDefault();
                if (playPromise) return;

                if (videoElement.paused) {
                    playPromise = videoElement.play();
                    playPromise.then(() => {
                        playPromise = null;
                    });
                } else {
                    videoElement.pause();
                }
                break;

            case 'l':
                event.preventDefault();
                // eslint-disable-next-line no-param-reassign
                videoElement.currentTime = normalizeTime(currentTime + jumpTime);
                videoElement.pause();
                break;

            default:
                break;
        }
    };
}

// Adapter making streamPlayer look like the old svpPlayer/jwplayer for consumers that use the legacy API
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createSvpPlayerAdapter = (streamPlayer: StreamPlayerInstance) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const timeListenerMap = new Map<(...args: any[]) => any, (...args: any[]) => any>();

    const jwPlayerStub = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        on(event: string, cb: (...args: any[]) => void) {
            if (event === 'levels') {
                streamPlayer.on('qualitychange', cb);
            }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        once(event: string, cb: (...args: any[]) => void) {
            if (event === 'meta') {
                streamPlayer.once('durationchange', cb);
            }
        },
        getQualityLevels() {
            return streamPlayer.state?.quality?.levels ?? [];
        },
        getCurrentQuality() {
            return streamPlayer.state?.quality?.currentLevel ?? -1;
        },
        setCurrentQuality(idx: number) {
            streamPlayer.selectQuality(idx);
        },
        setCurrentCaptions(idx: number) {
            if (idx === 0) {
                streamPlayer.selectSubtitle(null);
            }
        },
    };

    return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        on(event: string, cb: (...args: any[]) => void) {
            if (event === 'time') {
                const mapped = (currentTime: number) => cb(currentTime ?? streamPlayer.state?.time?.currentTime ?? 0);
                timeListenerMap.set(cb, mapped);
                streamPlayer.on('timeupdate', mapped);
            } else if (event === 'initialPlay') {
                streamPlayer.once('play', cb);
            } else {
                streamPlayer.on(event, cb);
            }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        off(event: string, cb: (...args: any[]) => void) {
            if (event === 'time') {
                const mapped = timeListenerMap.get(cb);
                if (mapped) {
                    streamPlayer.off('timeupdate', mapped);
                    timeListenerMap.delete(cb);
                }
            } else {
                streamPlayer.off(event, cb);
            }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        once(event: string, cb: (...args: any[]) => void) {
            if (event === 'assetPlay') {
                streamPlayer.once('play', cb);
            } else {
                streamPlayer.once(event, cb);
            }
        },
        getDuration() {
            return streamPlayer.state?.time?.duration ?? 0;
        },
        getJWPlayer() {
            return jwPlayerStub;
        },
        pause() {
            streamPlayer.pause();
        },
    };
};

export const createPlayer = (streamPlayer: StreamPlayerInstance) => {
    let initialPlay = false;
    let playerState: PlayerState = PLAYER_STATE.UNLOADED;
    let keysControlHandler: Maybe<ReturnType<typeof handleKeyPress>> = null;

    const setPlayerState = (newState: PlayerState) => {
        playerState = newState;
    };

    const svpPlayerAdapter = createSvpPlayerAdapter(streamPlayer);

    streamPlayer.on('error', () => {
        streamPlayer.destroy();
        setPlayerState(PLAYER_STATE.LOAD_ERROR);
    });

    streamPlayer.on('ready', () => {
        setPlayerState(PLAYER_STATE.LOADED);
        const videoEl = streamPlayer.media as HTMLVideoElement | null;
        if (videoEl) {
            const dur = streamPlayer.state?.time?.duration ?? 0;
            const audio = streamPlayer.media instanceof HTMLAudioElement;
            keysControlHandler = handleKeyPress(videoEl, audio, dur);
            document.addEventListener('keydown', keysControlHandler);
        }
    });

    streamPlayer.on('play', () => {
        initialPlay = true;
        setPlayerState(PLAYER_STATE.PLAYING);
    });

    const api = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onStateChange: (_newState: unknown) => {},

        get streamPlayer() {
            return streamPlayer;
        },

        get svpPlayer() {
            return svpPlayerAdapter;
        },

        get jwplayer() {
            return svpPlayerAdapter.getJWPlayer();
        },

        get videoElement(): HTMLVideoElement | null {
            const el = streamPlayer.media;
            return el instanceof HTMLVideoElement ? el : null;
        },

        get paused(): boolean {
            return streamPlayer.state?.playback?.paused ?? true;
        },

        get wasPlayed(): boolean {
            return initialPlay;
        },

        get currentTime(): number {
            return streamPlayer.state?.time?.currentTime ?? 0;
        },

        get duration(): number | undefined {
            return streamPlayer.state?.time?.duration;
        },

        get isLoaded(): boolean {
            return playerState >= PLAYER_STATE.LOADED;
        },

        get isAudio(): boolean {
            return streamPlayer.media instanceof HTMLAudioElement;
        },

        async load(asset: Asset) {
            return streamPlayer.playNext({ vendor: asset.provider, asset });
        },

        seek(time: number, play = false) {
            streamPlayer.seek(time).then(() => {
                if (!play) {
                    streamPlayer.pause();
                }
            });
        },

        seekLive(play = false) {
            const liveSyncEdge = streamPlayer.state?.live?.liveSyncEdge;
            if (liveSyncEdge != null) {
                streamPlayer.seek(liveSyncEdge).then(() => {
                    if (play) {
                        streamPlayer.play();
                    }
                });
            } else {
                // eslint-disable-next-line no-console
                console.warn('[StreamPlayer] seekLive: live sync edge not available');
            }
        },

        pause() {
            setPlayerState(PLAYER_STATE.PAUSED);
            streamPlayer.pause();
        },

        play(force = false) {
            setPlayerState(PLAYER_STATE.PLAYING);
            // `force` skips an armed countdown — `play({ force: false })` is
            // identical to a plain `play()`.
            streamPlayer.play({ force });
        },

        remove() {
            if (keysControlHandler) {
                document.removeEventListener('keydown', keysControlHandler, false);
            }
            streamPlayer.destroy();
        },

        setMaximumQuality() {
            const levels = streamPlayer.state?.quality?.levels ?? [];
            return new Promise<void>((resolve) => {
                if (levels.length > 1) {
                    streamPlayer.selectQuality(1);
                }
                resolve();
            });
        },

        getStreamDimensions(): { width: number; height: number } {
            const levels = streamPlayer.state?.quality?.levels ?? [];
            const currentLevel = streamPlayer.state?.quality?.currentLevel ?? -1;
            const { width = 0, height = 0 } = (currentLevel >= 0 && levels[currentLevel]) || {};
            return { width, height };
        },

        async generateImage({ mime = 'image/jpeg', quality = 1 }: { mime?: string; quality?: number } = {}): Promise<
            Maybe<Blob>
        > {
            if (!this.wasPlayed) {
                await new Promise<void>((resolve) => {
                    streamPlayer.once('seeked', resolve);
                    this.seek(this.currentTime);
                });
            }
            this.pause();

            const videoEl = this.videoElement;
            const { width = 0, height = 0 } = this.getStreamDimensions();
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!width || !height || !context || !videoEl) {
                return Promise.resolve(null);
            }
            canvas.width = width;
            canvas.height = height;

            return new Promise((resolve) => {
                setTimeout(() => {
                    context.drawImage(videoEl, 0, 0, width, height);
                    canvas.toBlob(
                        (blob) => {
                            resolve(blob);
                            canvas.remove();
                        },
                        mime,
                        quality
                    );
                }, 500);
            });
        },
    };

    return api;
};

export type NewPlayerApi = ReturnType<typeof createPlayer>;
